import React, { useState } from 'react';
import { Box, Typography, Paper, Checkbox, Button, styled, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Vehicle } from '../../state/types';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';
import Vehicle360Modal from './Vehicle360Modal';
import { useVehiclesContext } from '../../context/VehiclesContext';
import { useVehicleTableLogic } from '../../hooks/vehicles/useVehicleTableLogic';

const VehicleCardList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { vehicles, setSelectedVehicles } = useVehiclesContext();

  const {
    openModal,
    modalVehicle,
    thumbnailErrors,
    handleThumbnailError,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    isSelected,
  } = useVehicleTableLogic();

  const handleRowClick = (clientId: string, assetId: string) => {
    navigate(`/vehicle/${clientId}/${assetId}`);
  };

  const getSyndicationStep = (vehicle: Vehicle) => {
    if (!vehicle || !vehicle.syndicationSteps || !vehicle.syndicationCurrentStep) return ''

    if (vehicle.syndicationCurrentStep === "COMPLETE") return vehicle?.syndicationSteps[0]

    return vehicle.syndicationCurrentStep
  }

  const StatusTag = styled('span')(
    ({
      theme,
      status,
    }: {
      theme?: any;
      status: 'COMPLETE' | 'ERROR' | 'PENDING' | 'NOT STARTED' | string;
    }) => {
      let bgColor = theme.palette.grey[500]; // Default for NOT STARTED

      switch (status) {
        case 'COMPLETE':
          bgColor = theme.palette.success.main;
          break;
        case 'ERROR':
          bgColor = theme.palette.error.main;
          break;
        case 'PENDING':
          bgColor = theme.palette.warning.main;
          break;
        case 'NOT STARTED':
          bgColor = theme.palette.grey[400];
          break;
        default:
          bgColor = theme.palette.grey[500];
      }

      return {
        backgroundColor: bgColor,
        color: theme.palette.background.paper,
        borderRadius: '3px',
        padding: '4px 8px',
        display: 'inline-block',
        fontSize: '12px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
      };
    }
  );

  const SpinningButton = styled(Button)(({ theme }) => ({
    borderRadius: '5px',
    padding: '12px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.warning.main,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 4px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  }));

  return (
    <>
      {vehicles.map(vehicle => (
        <Paper key={vehicle.assetId} sx={{ mb: 2, p: 2 }}>
          <Box display="flex" flexDirection="column" gap={1}
            onClick={() => handleRowClick(vehicle.clientId, vehicle.assetId)}>
            <Box display="flex" alignItems="center">
              <Checkbox
                onClick={(e) => handleCheckboxClick(e, vehicle)}
                checked={isSelected(vehicle)}
                size="small"
                sx={{ '&.Mui-checked': { color: "darkred" } }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  textAlign: 'center',
                  marginBottom: 0.5,
                  wordBreak: 'break-word',
                  userSelect: 'text', // ✅ ensure highlight works
                  cursor: 'text',     // ✅ doesn’t look like a link
                }}
                onClick={(e) => e.stopPropagation()} // ✅ block row navigation
              >
                {vehicle.vin}
              </Typography>
            </Box>

            <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' /* 16:9 */ }}>
              {vehicle.thumbnail && !thumbnailErrors[vehicle.assetId] ? (
                <Box
                  component="img"
                  src={vehicle.thumbnail}
                  alt="vehicle thumbnail"
                  onError={() => handleThumbnailError(vehicle.assetId)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    zIndex: 10,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    zIndex: 10,
                  }}
                >
                  <Typography>No Thumbnail</Typography>
                </Box>
              )}
            </Box>


            <Grid container spacing={2} mt={1}>
              {/* Image Counts Column */}
              <Grid item xs={12} sm={6}>
                {[
                  { label: "Exterior", count: vehicle.imageExterior?.length ?? 0 },
                  { label: "Cropped", count: vehicle.imageCrop?.length ?? 0 },
                  { label: "Featured", count: vehicle.imageFeatured?.length ?? 0 },
                  { label: "Manual", count: vehicle.imageManual?.length ?? 0 },
                  { label: "Pano", count: vehicle.imagePano?.length ?? 0 },
                  { label: "UC", count: vehicle.imageUndercarriage?.length ?? 0 },
                  { label: "BGR", count: vehicle.imageBgr?.length ?? 0 },
                ].map(({ label, count }) => (
                  <Typography key={label}>{label}: {count}</Typography>
                ))}
              </Grid>

              {/* Meta / Status Column */}
              <Grid item xs={12} sm={6}>
                <Box display="flex" flexDirection={'column'} gap={1} mb={1}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" fontWeight="bold">Processing:</Typography>
                    <StatusTag
                      status={vehicle.processingStatus || ''}
                      title={vehicle.processingError || undefined}
                    >
                      {vehicle.processingCurrentStep}
                    </StatusTag>
                  </Box>

                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" fontWeight="bold">Syndication:</Typography>
                    <StatusTag
                      status={vehicle.syndicationStatus || ''}
                      title={vehicle.syndicationError || undefined}
                    >
                      {getSyndicationStep(vehicle)}
                    </StatusTag>
                  </Box>
                </Box>

                <Typography variant="body2">Posted: {new Date(+vehicle.created_at * 1000).toLocaleString()}</Typography>
                <Typography variant="body2">Config: {vehicle.config}</Typography>
                <Typography variant="body2">
                  {vehicle.imagePano?.length ? (
                    <SpinningButton onClick={(e) => handle360Click(e, vehicle.clientId, vehicle.assetId)}>
                      360 <SyncTwoToneIcon sx={{ fontSize: '18px', ml: 1 }} />
                    </SpinningButton>
                  ) : (
                    'No Panos'
                  )}
                </Typography>
              </Grid>
            </Grid>

          </Box>
        </Paper>
      ))}

      <Vehicle360Modal
        open={openModal}
        handleClose={handleCloseModal}
        clientId={modalVehicle?.clientId || null}
        assetId={modalVehicle?.assetId || null}
      />
    </>
  );
};

export default VehicleCardList;
